import { Link } from 'react-router-dom';
import FadeIn from '@/components/FadeIn';
import { useConsultation } from '@/components/ConsultationModal';
import { IMAGES } from '@/lib/constants';
import { Wine, Hotel, Sparkles, Users, Check, ArrowRight, ArrowLeft, Play } from 'lucide-react';
import { useState, useRef } from 'react';

const tiers = [
  {
    icon: Hotel,
    number: '01',
    title: 'Hospitality Partners',
    subtitle: 'Restaurants, Spas & Hotels',
    description: 'Partner with us to offer premium wine and champagne to your guests. We provide the inventory and you earn a commission on every bottle sold. No upfront cost, no inventory risk.',
    details: [
      'Curated wine and champagne selection for your venue',
      'Commission on every bottle sold',
      'No upfront inventory investment',
      'Dedicated account support',
      'Custom wine lists tailored to your clientele',
      'Seamless ordering and fulfillment',
    ],
  },
  {
    icon: Sparkles,
    number: '02',
    title: 'Influencer Brand Partners',
    subtitle: 'Your Brand, Our Infrastructure',
    featured: true,
    description: 'Open your own branded wine store. We handle all inventory, fulfillment, and accounting while you build your brand and grow your audience. A true partnership for creators ready to enter the wine business.',
    details: [
      'Launch your own branded wine store',
      'Full inventory management handled by us',
      'Order fulfillment and shipping',
      'Complete accounting and financial reporting',
      'Custom label and branding support',
      'Scalable partnership as your audience grows',
    ],
  },
  {
    icon: Users,
    number: '03',
    title: 'Sales Brokers',
    subtitle: 'Wholesale & Retail Commission',
    description: 'Join our network as an individual sales broker. Sell wine wholesale and retail to your contacts and earn a commission on every transaction. Ideal for professionals with strong networks in hospitality, events, or luxury goods.',
    details: [
      'Sell wine wholesale and retail',
      'Earn commission on every transaction',
      'Access to premium wine portfolio',
      'Sales support and training materials',
      'Flexible, independent work structure',
      'No inventory or fulfillment responsibilities',
    ],
  },
];

export default function WinePage() {
  const { setOpen } = useConsultation();
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlayVideo = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div data-testid="wine-page">
      {/* HERO */}
      <section className="relative py-24 lg:py-32 bg-wc-black overflow-hidden" data-testid="wine-hero">
        <div className="absolute inset-0 opacity-20">
          <img src={IMAGES.darkTexture} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <Link
              to="/associate-network"
              className="inline-flex items-center gap-2 font-body text-sm text-white/50 hover:text-white/80 transition-colors duration-300 mb-8"
              data-testid="wine-back-link"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Associate Network
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <Wine className="w-6 h-6 text-wc-gold" strokeWidth={1.5} />
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold">Fine Wine & Champagne</p>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white max-w-4xl leading-[1.1]">
              Premium Wine Partnerships Through Trusted Associates
            </h1>
            <p className="font-body text-base lg:text-lg text-white/55 mt-6 max-w-2xl leading-relaxed">
              Through our partner network, William Covington & Associates offers three distinct tiers for businesses and individuals looking to participate in the fine wine and champagne market.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* VIDEO SECTION */}
      <section className="py-24 lg:py-32 bg-white" data-testid="wine-video">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">See It in Action</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-wc-black">
                Wine & Champagne Ordering Service
              </h2>
            </div>
          </FadeIn>
          <FadeIn delay={100}>
            <div className="relative bg-wc-black aspect-video cursor-pointer group" onClick={handlePlayVideo} data-testid="wine-video-player">
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                poster=""
                playsInline
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                controls={isPlaying}
              >
                <source src={IMAGES.wineVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              {!isPlaying && (
                <div className="absolute inset-0 flex items-center justify-center bg-wc-black/30 group-hover:bg-wc-black/40 transition-colors duration-300">
                  <div className="w-20 h-20 border-2 border-white/80 flex items-center justify-center group-hover:border-wc-gold group-hover:scale-110 transition-all duration-300">
                    <Play className="w-8 h-8 text-white ml-1" fill="white" strokeWidth={0} />
                  </div>
                </div>
              )}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* THREE TIERS */}
      <section className="py-24 lg:py-32 bg-wc-bg-alt" data-testid="wine-tiers">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Partnership Tiers</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-wc-black mb-4">
                Three Ways to Partner
              </h2>
              <p className="font-body text-base text-wc-muted max-w-2xl mx-auto leading-relaxed">
                Whether you operate a venue, build audiences, or have a strong sales network, there's a wine partnership tier designed for you.
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-px bg-wc-border">
            {tiers.map((tier, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className={`bg-white p-10 lg:p-12 h-full flex flex-col ${tier.featured ? 'border-t-2 border-t-wc-gold' : ''}`}>
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <tier.icon className="w-7 h-7 text-wc-gold" strokeWidth={1.5} />
                      <span className="font-accent text-xs text-wc-muted tracking-[0.2em]">{tier.number}</span>
                    </div>
                    <h3 className="font-heading text-2xl font-normal text-wc-black mb-1">{tier.title}</h3>
                    <p className="font-accent text-xs uppercase tracking-[0.15em] text-wc-gold mb-4">{tier.subtitle}</p>
                    <p className="font-body text-sm text-wc-muted leading-relaxed mb-8">{tier.description}</p>
                  </div>
                  <ul className="space-y-3 mb-10 flex-1">
                    {tier.details.map((detail, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-wc-gold flex-shrink-0 mt-0.5" strokeWidth={2} />
                        <span className="font-body text-sm text-wc-black">{detail}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setOpen(true)}
                    className={`w-full py-3.5 font-body text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                      tier.featured
                        ? 'bg-wc-gold text-white hover:bg-wc-gold-hover'
                        : 'bg-wc-black text-white hover:bg-wc-charcoal'
                    }`}
                    data-testid={`wine-tier-cta-${i}`}
                  >
                    Inquire About This Tier
                  </button>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* WILLIAM IMAGE + CTA */}
      <section className="py-24 lg:py-32 bg-white" data-testid="wine-about">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <img
                src={IMAGES.williamGlasses}
                alt="William K. Covington"
                className="w-full aspect-[4/3] object-cover object-top"
                loading="lazy"
              />
            </FadeIn>
            <FadeIn delay={150}>
              <div>
                <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Your Partner</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-wc-black mb-6">
                  Wine Meets Financial Expertise
                </h2>
                <p className="font-body text-base text-wc-muted leading-relaxed mb-6">
                  Through our trusted partner, William Covington & Associates brings the same level of professionalism and financial organization to the wine business. We handle the accounting, inventory management, and fulfillment infrastructure so our partners can focus on what they do best — selling exceptional wine.
                </p>
                <p className="font-body text-base text-wc-muted leading-relaxed mb-10">
                  Whether you're a restaurant owner looking to enhance your wine program, an influencer ready to launch your own brand, or a sales professional with a strong network, we have a partnership tier built for you.
                </p>
                <button
                  onClick={() => setOpen(true)}
                  className="bg-wc-black text-white px-10 py-4 font-body text-sm uppercase tracking-[0.15em] hover:bg-wc-charcoal transition-colors duration-300"
                  data-testid="wine-cta-button"
                >
                  Schedule a Consultation
                </button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
